import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Grid,
  Typography,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Box,
  Tooltip,
  IconButton,
} from "@material-ui/core";
import {
  ShoppingCart,
  AttachMoney,
  LocalShipping,
  CheckCircle,
  Cancel,
  Timer,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
} from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import * as pharmaApi from "../../services/pharmaApi";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

// Auto-refresh interval options
const REFRESH_OPTIONS = [
  { value: 0, label: "Desativado" },
  { value: 15000, label: "15 segundos" },
  { value: 30000, label: "30 segundos" },
  { value: 60000, label: "1 minuto" },
  { value: 120000, label: "2 minutos" },
  { value: 300000, label: "5 minutos" },
];

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  card: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(2),
  },
  cardIcon: {
    fontSize: 48,
    marginBottom: theme.spacing(1),
  },
  cardValue: {
    fontSize: 28,
    fontWeight: "bold",
  },
  cardLabel: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  filterContainer: {
    marginBottom: theme.spacing(3),
    display: "flex",
    gap: theme.spacing(2),
  },
  pendingCard: {
    backgroundColor: "#fff3e0",
    "& $cardIcon": { color: "#ff9800" },
  },
  preparingCard: {
    backgroundColor: "#e3f2fd",
    "& $cardIcon": { color: "#2196f3" },
  },
  readyCard: {
    backgroundColor: "#e8f5e9",
    "& $cardIcon": { color: "#4caf50" },
  },
  deliveredCard: {
    backgroundColor: "#f3e5f5",
    "& $cardIcon": { color: "#9c27b0" },
  },
  cancelledCard: {
    backgroundColor: "#ffebee",
    "& $cardIcon": { color: "#f44336" },
  },
  revenueCard: {
    backgroundColor: "#e8f5e9",
    "& $cardIcon": { color: "#2e7d32" },
  },
  refreshing: {
    animation: "$spin 1s linear infinite",
  },
  "@keyframes spin": {
    from: { transform: "rotate(0deg)" },
    to: { transform: "rotate(360deg)" },
  },
}));

const PdvDashboard = () => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState("");
  const [dateRange, setDateRange] = useState("today");
  const [stats, setStats] = useState({
    totalOrders: 0,
    pendingOrders: 0,
    confirmedOrders: 0,
    preparingOrders: 0,
    readyOrders: 0,
    deliveredOrders: 0,
    cancelledOrders: 0,
    totalRevenue: 0,
    averageTicket: 0,
  });
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(() => {
    const saved = localStorage.getItem("pdvDashboardRefreshInterval");
    return saved ? parseInt(saved, 10) : 30000;
  });
  const refreshIntervalRef = useRef(null);

  useEffect(() => {
    const fetchStores = async () => {
      try {
        // Use PharmaFlow API
        const data = await pharmaApi.getStores();
        const storesList = data || [];
        setStores(storesList);
        // If more than one store, default to "all", otherwise select the only one
        if (storesList.length > 1) {
          setSelectedStore("all");
        } else if (storesList.length === 1) {
          setSelectedStore(storesList[0].id);
        }
      } catch (err) {
        toastError(err);
      }
    };
    fetchStores();
  }, []);

  const fetchDashboard = useCallback(async (showLoadingIndicator = false) => {
    if (!selectedStore && stores.length === 0) return;

    if (showLoadingIndicator) {
      setLoading(true);
    }
    setIsRefreshing(true);

    try {
      const now = new Date();
      let startDate, endDate;

      switch (dateRange) {
        case "today":
          startDate = new Date(now.setHours(0, 0, 0, 0)).toISOString();
          endDate = new Date().toISOString();
          break;
        case "week":
          startDate = new Date(now.setDate(now.getDate() - 7)).toISOString();
          endDate = new Date().toISOString();
          break;
        case "month":
          startDate = new Date(now.setMonth(now.getMonth() - 1)).toISOString();
          endDate = new Date().toISOString();
          break;
        default:
          startDate = new Date(now.setHours(0, 0, 0, 0)).toISOString();
          endDate = new Date().toISOString();
      }

      const params = { startDate, endDate };
      // Only add storeId if a specific store is selected (not "all")
      if (selectedStore && selectedStore !== "all") {
        params.storeId = selectedStore;
      }
      // Use PharmaFlow API
      const data = await pharmaApi.getOrdersDashboard(params);
      // Map response to expected format
      setStats({
        totalOrders: data.today?.ordersCount || 0,
        pendingOrders: data.statusCounts?.pending || 0,
        confirmedOrders: data.statusCounts?.confirmed || 0,
        preparingOrders: data.statusCounts?.preparing || 0,
        readyOrders: data.statusCounts?.ready || 0,
        deliveredOrders: data.statusCounts?.delivered || 0,
        cancelledOrders: data.statusCounts?.cancelled || 0,
        totalRevenue: data.today?.totalSales || 0,
        averageTicket: data.today?.ordersCount > 0
          ? data.today?.totalSales / data.today?.ordersCount
          : 0,
        recentOrders: data.recentOrders || [],
      });
      setLastRefresh(new Date());
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [selectedStore, dateRange, stores.length]);

  // Initial fetch and when filters change
  useEffect(() => {
    fetchDashboard(true);
  }, [fetchDashboard]);

  // Auto-refresh interval
  useEffect(() => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }

    if (refreshInterval > 0) {
      refreshIntervalRef.current = setInterval(() => {
        fetchDashboard(false);
      }, refreshInterval);
    }

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [fetchDashboard, refreshInterval]);

  const handleRefreshIntervalChange = (e) => {
    const newInterval = e.target.value;
    setRefreshInterval(newInterval);
    localStorage.setItem("pdvDashboardRefreshInterval", newInterval);
  };

  // Socket listener for real-time updates
  useEffect(() => {
    if (!socket || !user?.companyId) return;

    const companyId = user.companyId;
    const onPdvOrder = (data) => {
      if (data.action === "update" || data.action === "create") {
        // Refresh dashboard when order changes
        fetchDashboard(false);
      }
    };

    socket.on(`company-${companyId}-pdvOrder`, onPdvOrder);
    return () => {
      socket.off(`company-${companyId}-pdvOrder`, onPdvOrder);
    };
  }, [socket, user, fetchDashboard]);

  const handleManualRefresh = () => {
    fetchDashboard(false);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const StatCard = ({ icon: Icon, value, label, className }) => (
    <Card className={`${classes.card} ${className}`}>
      <Icon className={classes.cardIcon} />
      <Typography className={classes.cardValue}>{value}</Typography>
      <Typography className={classes.cardLabel}>{label}</Typography>
    </Card>
  );

  return (
    <MainContainer>
      <MainHeader>
        <Title>OtimizAPP Pharma - Dashboard</Title>
        <MainHeaderButtonsWrapper>
          <Tooltip title={`Última atualização: ${lastRefresh.toLocaleTimeString("pt-BR")}`}>
            <IconButton
              onClick={handleManualRefresh}
              disabled={isRefreshing}
              size="small"
            >
              <RefreshIcon className={isRefreshing ? classes.refreshing : ""} />
            </IconButton>
          </Tooltip>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper}>
        <Box className={classes.filterContainer}>
          <FormControl variant="outlined" style={{ minWidth: 200 }}>
            <InputLabel>Loja</InputLabel>
            <Select
              value={selectedStore}
              onChange={(e) => setSelectedStore(e.target.value)}
              label="Loja"
            >
              {stores.length > 1 && (
                <MenuItem value="all">Todas as lojas</MenuItem>
              )}
              {stores.map((store) => (
                <MenuItem key={store.id} value={store.id}>
                  {store.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl variant="outlined" style={{ minWidth: 150 }}>
            <InputLabel>Período</InputLabel>
            <Select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              label="Período"
            >
              <MenuItem value="today">Hoje</MenuItem>
              <MenuItem value="week">Últimos 7 dias</MenuItem>
              <MenuItem value="month">Último mês</MenuItem>
            </Select>
          </FormControl>
          <FormControl variant="outlined" style={{ minWidth: 150 }}>
            <InputLabel>Auto-refresh</InputLabel>
            <Select
              value={refreshInterval}
              onChange={handleRefreshIntervalChange}
              label="Auto-refresh"
            >
              {REFRESH_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                icon={ShoppingCart}
                value={stats.totalOrders}
                label="Total de Pedidos"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                icon={Timer}
                value={stats.pendingOrders}
                label="Aguardando"
                className={classes.pendingCard}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                icon={LocalShipping}
                value={stats.preparingOrders}
                label="Em Preparo"
                className={classes.preparingCard}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                icon={CheckCircle}
                value={stats.readyOrders}
                label="Prontos"
                className={classes.readyCard}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                icon={CheckCircle}
                value={stats.deliveredOrders}
                label="Entregues"
                className={classes.deliveredCard}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                icon={Cancel}
                value={stats.cancelledOrders}
                label="Cancelados"
                className={classes.cancelledCard}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StatCard
                icon={AttachMoney}
                value={formatCurrency(stats.totalRevenue)}
                label="Faturamento"
                className={classes.revenueCard}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StatCard
                icon={AttachMoney}
                value={formatCurrency(stats.averageTicket)}
                label="Ticket Médio"
              />
            </Grid>
          </Grid>
        )}
      </Paper>
    </MainContainer>
  );
};

export default PdvDashboard;
